import React from "react";
import PopUpComponent from "../../components/PopUp/PopUpComponent";
import { useStockIn } from "./useStockIn";
import { hasRole } from "../../util/utilFunction";
import "./StockIn.css"; // CSS lama, jangan diubah/dihapus

/* ------------------------------------------------------------------ */
/* Helper                                                              */
/* ------------------------------------------------------------------ */

const TITLES = {
  BahanBaku: "INPUT BAHAN",
  Crusher: "INPUT CRUSHER",
  Pewarna: "INPUT PEWARNA",
  RiwayatBahanBaku: "RIWAYAT BAHAN BAKU",
  RiwayatCrusher: "RIWAYAT CRUSHER",
  RiwayatPewarna: "RIWAYAT PEWARNA",
};

const formatDot = (value) =>
  value && value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const numberWithCommas = (data) =>
  data &&
  data.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });

const switchDotsAndCommas = (s) => {
  function switcher(match) {
    return match === "," ? "." : ",";
  }
  return s && s.replaceAll(/\.|\,/g, switcher);
};

/* ------------------------------------------------------------------ */
/* Komponen kecil                                                      */
/* ------------------------------------------------------------------ */

const Field = ({ label, required, full, children }) => (
  <div className={full ? "si_field si_field_full" : "si_field"}>
    <p className="si_label">
      {label}
      {required && <span className="si_required">*</span>}
    </p>
    {children}
  </div>
);

const RadioOption = ({ value, label, checked, onSelect }) => (
  <label className={checked ? "si_radio si_radio_checked" : "si_radio"}>
    <input
      type="radio"
      value={value}
      checked={checked}
      onChange={() => onSelect(value)}
    />
    <span>{label}</span>
  </label>
);

const PopUpMessage = ({ message, onClose }) => (
  <PopUpComponent>
    <p>{message}</p>
    <div className="si_popup_footer">
      <button className="si_btn_primary" onClick={onClose}>
        OK
      </button>
    </div>
  </PopUpComponent>
);

/* Tabel riwayat (dipakai untuk Bahan Baku, Crusher, dan Pewarna) */
const LogTable = ({ rows, filterDate, setFilterDate }) => (
  <>
    <div className="si_filter">
      <p className="si_label">Pilih Tanggal</p>
      <input
        type="date"
        className="si_input"
        value={filterDate}
        onChange={(e) => setFilterDate(e.target.value)}
      />
    </div>

    <div className="si_table_wrap">
      <table className="si_table">
        <thead>
          <tr>
            <th className="si_td_no">No</th>
            <th>Customer</th>
            <th>Material</th>
            <th>Jumlah</th>
            <th>Status</th>
            <th>Operator</th>
            <th>Keterangan</th>
          </tr>
        </thead>
        <tbody>
          {rows &&
            rows.map((e, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td className="si_td_text">{e && e.company}</td>
                <td className="si_td_text">{e && e.material}</td>
                <td>
                  {formatDot(
                    switchDotsAndCommas(numberWithCommas(e && e.dataWeight))
                  )}
                </td>
                <td>
                  <span
                    className={
                      e && e.statusBahan === "Masuk"
                        ? "si_chip si_chip_in"
                        : "si_chip si_chip_out"
                    }
                  >
                    {e && e.statusBahan}
                  </span>
                </td>
                <td>
                  {e && (e.updatedBy || "").split(" ").slice(0, 1).join(" ")}
                </td>
                <td className="si_td_text">{e && e.keterangan}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  </>
);

/* ------------------------------------------------------------------ */
/* Halaman                                                             */
/* ------------------------------------------------------------------ */

const StockIn = () => {
  const {
    dataUser,
    companyRequest,
    setCompanyRequest,
    setMaterialName,
    handleSubmitRequest,
    dataBahan,
    popUpRequest,
    jumlahRequest,
    setJumlahRequest,
    setKeteranganRequestStockIn,
    dataListBahan,
    handleKirimRequestIn,
    materialName,
    popUpErrorRequest,
    keteranganRequestStockIn,
    statusBahan,
    setStatusBahan,
    subMenuStockIn,
    setSubMenuStockIn,
    dataCrusher,
    dataLogBahanCrusher,
    filterDate,
    setFilterDate,
    dataPewarna,
  } = useStockIn();

  // ---------- Hak akses ----------
  // Tab dan isi: Bahan Baku + Pewarna (daftarnya identik)
  const canBahan = hasRole(
    dataUser,
    "warehouse_admin",
    "warehouse_mixing",
    "warehouse_gbj_admin",
    "ppic_user",
    "super_user"
  );

  // Tab dan isi: Crusher
  const canCrusher = hasRole(
    dataUser,
    "warehouse_admin",
    "warehouse_mixing",
    "warehouse_crusher",
    "ppic_user",
    "super_user"
  );

  // Tab dan isi: Riwayat Bahan Baku + Riwayat Pewarna (daftarnya identik)
  const canRiwayatBahan = hasRole(
    dataUser,
    "warehouse_admin",
    "warehouse_mixing",
    "ppic_admin",
    "ppic_user",
    "super_user"
  );

  // Tab dan isi: Riwayat Crusher
  const canRiwayatCrusher = hasRole(
    dataUser,
    "warehouse_admin",
    "warehouse_mixing",
    "warehouse_crusher",
    "ppic_admin",
    "ppic_user",
    "super_user"
  );

  // Input "Pilih Tanggal" di form Bahan Baku dan Pewarna
  const canPilihTanggal = hasRole(dataUser, "ppic_user", "super_user");

  // ---------- tab ----------
  const tabs = [
    { key: "BahanBaku", label: "Bahan Baku", show: canBahan },
    { key: "Crusher", label: "Crusher", show: canCrusher },
    { key: "Pewarna", label: "Pewarna", show: canBahan },
    {
      key: "RiwayatBahanBaku",
      label: "Riwayat Bahan Baku",
      show: canRiwayatBahan,
    },
    {
      key: "RiwayatCrusher",
      label: "Riwayat Crusher",
      show: canRiwayatCrusher,
    },
    { key: "RiwayatPewarna", label: "Riwayat Pewarna", show: canRiwayatBahan },
  ];

  // ---------- form input (Bahan Baku / Crusher / Pewarna) ----------
  const renderForm = ({
    statusLabel,
    showDate,
    itemLabel,
    itemPlaceholder,
    items,
  }) => (
    <div className="si_card">
      <div className="si_form">
        {showDate && canPilihTanggal && (
          <Field label="Pilih Tanggal">
            <input
              type="date"
              className="si_input"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </Field>
        )}

        <Field label={statusLabel} required full>
          <div className="si_radio_group">
            <RadioOption
              value="Keluar"
              label="Keluar"
              checked={statusBahan === "Keluar"}
              onSelect={setStatusBahan}
            />
            <RadioOption
              value="Masuk"
              label="Masuk"
              checked={statusBahan === "Masuk"}
              onSelect={setStatusBahan}
            />
          </div>
        </Field>

        <Field label="Perusahaan" required>
          <select
            className="si_select"
            value={companyRequest || ""}
            onChange={(e) => setCompanyRequest(e.target.value)}
          >
            <option value="" disabled>
              Pilih Nama Perusahaan
            </option>
            {dataListBahan &&
              dataListBahan.map((data, i) => (
                <option value={data.company} key={i}>
                  {data.company}
                </option>
              ))}
          </select>
        </Field>

        <Field label={itemLabel} required>
          <select
            className="si_select"
            value={materialName || ""}
            onChange={(e) => setMaterialName(e.target.value)}
          >
            <option value="" disabled>
              {itemPlaceholder}
            </option>
            {items &&
              items.map((data, i) => (
                <option value={data.material} key={i}>
                  {data.material}
                </option>
              ))}
          </select>
        </Field>

        <Field label="Jumlah (Kg)" required>
          <input
            type="number"
            className="si_input"
            placeholder="Masukkan Jumlah (Kg)"
            value={jumlahRequest}
            onChange={(e) => setJumlahRequest(e.target.value)}
            onWheel={(e) => e.target.blur()}
          />
        </Field>

        <Field label="Keterangan" full>
          <textarea
            className="si_textarea"
            placeholder="Masukkan Keterangan Jika Diperlukan"
            onChange={(e) => setKeteranganRequestStockIn(e.target.value)}
            value={keteranganRequestStockIn}
          />
        </Field>
      </div>

      <div className="si_form_footer">
        <button
          className="si_btn_primary"
          onClick={() => handleKirimRequestIn()}
        >
          Kirim
        </button>
      </div>
    </div>
  );

  const logProps = { filterDate, setFilterDate };

  return (
    <div className="container_dashboard">
      {popUpRequest && (
        <PopUpMessage
          message="Terima Kasih! Anda Telah Men-update Stock Masuk Bahan."
          onClose={() => handleSubmitRequest("OK")}
        />
      )}
      {popUpErrorRequest && (
        <PopUpMessage
          message="Mohon pastikan semua kolom yang Berbintang Merah Terisi !"
          onClose={() => handleSubmitRequest("OK")}
        />
      )}

      <p className="si_title">{TITLES[subMenuStockIn] || ""}</p>

      <div className="si_tabs">
        {tabs
          .filter((tab) => tab.show)
          .map((tab) => (
            <p
              key={tab.key}
              className={
                subMenuStockIn === tab.key ? "si_tab si_tab_active" : "si_tab"
              }
              onClick={() => setSubMenuStockIn(tab.key)}
            >
              {tab.label}
            </p>
          ))}
      </div>

      {subMenuStockIn === "BahanBaku" &&
        canBahan &&
        renderForm({
          statusLabel: "Keluar / Masuk Bahan",
          showDate: true,
          itemLabel: "Nama Bahan",
          itemPlaceholder: "Pilih Nama Bahan",
          items: dataBahan,
        })}

      {subMenuStockIn === "Crusher" &&
        canCrusher &&
        renderForm({
          statusLabel: "Keluar / Masuk Crusher",
          showDate: false,
          itemLabel: "Nama Bahan",
          itemPlaceholder: "Pilih Crusher",
          items: dataCrusher,
        })}

      {subMenuStockIn === "Pewarna" &&
        canBahan &&
        renderForm({
          statusLabel: "Keluar / Masuk Pewarna",
          showDate: true,
          itemLabel: "Nama Pewarna",
          itemPlaceholder: "Pilih Pewarna",
          items: dataPewarna,
        })}

      {subMenuStockIn === "RiwayatBahanBaku" && canRiwayatBahan && (
        <LogTable
          rows={dataLogBahanCrusher && dataLogBahanCrusher.bahan}
          {...logProps}
        />
      )}

      {subMenuStockIn === "RiwayatCrusher" && canRiwayatCrusher && (
        <LogTable
          rows={dataLogBahanCrusher && dataLogBahanCrusher.crusher}
          {...logProps}
        />
      )}

      {subMenuStockIn === "RiwayatPewarna" && canRiwayatBahan && (
        <LogTable
          rows={dataLogBahanCrusher && dataLogBahanCrusher.pewarna}
          {...logProps}
        />
      )}
    </div>
  );
};

export default StockIn;
